import React, { useEffect } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid2,
  Paper,
  Switch,
  TextField,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { observer, useLocalObservable } from "mobx-react";
import { runInAction } from "mobx";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { CategoryStore } from "../../../stores/CategoryStore";
import { CatalogOptionsStore } from "../../../stores/CatalogOptionsStore";
import { CategoryOptionMapping } from "../../../../../models/Categories/types";
import {
  CreateOptionValueBody,
  OptionValueApi,
  getApiErrorMessage,
  validateCreateOptionValueBody,
} from "../../../../../models/CatalogOptions/types";
import {
  UpdateVariantsInput,
  buildVariantCombos,
  getApiErrorMessage as getProductApiErrorMessage,
} from "../../../../../models/CatalogProducts/types";
import { useNotification } from "../../../../../utils/NotificationsContext";

export interface SkuBuilderOptionState {
  mapping: CategoryOptionMapping;
  enabled: boolean;
  loadingValues: boolean;
  /** Current search results from GET /options/:id/values */
  values: OptionValueApi[];
  /** Debounced search input for this option's values */
  valueSearch: string;
  selectedValueIds: number[];
  /** Persists selected value objects across search refreshes */
  selectedValues: OptionValueApi[];
}

interface ValueDraft {
  value: string;
  sku_code: string;
  position: string;
  hex: string;
  is_active: boolean;
}

const emptyDraft = (): ValueDraft => ({
  value: "",
  sku_code: "",
  position: "0",
  hex: "",
  is_active: true,
});

interface ProductSkuBuilderProps {
  categoryId: number | null;
  disabled?: boolean;
  /** Server-generated product SKU (read-only). */
  serverSku?: string | null;
  /** Bump to clear option/value selections after a successful save. */
  resetKey?: number;
  onVariantsChange: (variants: UpdateVariantsInput["variants"]) => void;
}

const ProductSkuBuilder: React.FC<ProductSkuBuilderProps> = ({
  categoryId,
  disabled = false,
  serverSku,
  resetKey = 0,
  onVariantsChange,
}) => {
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const colors = theme.palette.customColors;
  const categoryStore = React.useRef(new CategoryStore()).current;
  const catalogOptionsStore = React.useRef(new CatalogOptionsStore()).current;

  const ui = useLocalObservable(() => ({
    loading: false,
    optionSearch: "",
    options: [] as SkuBuilderOptionState[],
    variantPreview: [] as UpdateVariantsInput["variants"],
    addingForOptionId: null as number | null,
    addDraft: emptyDraft() as ValueDraft,
    savingValue: false,
    setOptionSearch(value: string) {
      this.optionSearch = value;
    },
    setOptions(next: SkuBuilderOptionState[]) {
      this.options = next;
      this.recompute();
    },
    toggleOption(optionId: number) {
      this.options = this.options.map((o) =>
        o.mapping.option_id === optionId
          ? {
              ...o,
              enabled: !o.enabled,
              selectedValueIds: !o.enabled ? o.selectedValueIds : [],
              selectedValues: !o.enabled ? o.selectedValues : [],
              valueSearch: !o.enabled ? o.valueSearch : "",
              values: !o.enabled ? o.values : [],
              loadingValues: !o.enabled,
            }
          : o,
      );
      if (this.addingForOptionId === optionId) {
        this.addingForOptionId = null;
        this.addDraft = emptyDraft();
      }
      this.recompute();
    },
    setValueSearch(optionId: number, value: string) {
      this.options = this.options.map((o) =>
        o.mapping.option_id === optionId ? { ...o, valueSearch: value } : o,
      );
    },
    toggleValue(optionId: number, valueId: number) {
      this.options = this.options.map((o) => {
        if (o.mapping.option_id !== optionId || !o.enabled) return o;
        const has = o.selectedValueIds.includes(valueId);
        if (has) {
          return {
            ...o,
            selectedValueIds: o.selectedValueIds.filter((id) => id !== valueId),
            selectedValues: o.selectedValues.filter((v) => v.id !== valueId),
          };
        }
        const valueObj =
          o.values.find((v) => v.id === valueId) ||
          o.selectedValues.find((v) => v.id === valueId);
        if (!valueObj) return o;
        return {
          ...o,
          selectedValueIds: [...o.selectedValueIds, valueId],
          selectedValues: [...o.selectedValues, valueObj],
        };
      });
      this.recompute();
    },
    setOptionValues(optionId: number, values: OptionValueApi[]) {
      this.options = this.options.map((o) => {
        if (o.mapping.option_id !== optionId) return o;
        return {
          ...o,
          values,
          loadingValues: false,
        };
      });
    },
    setLoadingValues(optionId: number, loading: boolean) {
      this.options = this.options.map((o) =>
        o.mapping.option_id === optionId ? { ...o, loadingValues: loading } : o,
      );
    },
    openAddForm(optionId: number) {
      this.addingForOptionId = optionId;
      this.addDraft = emptyDraft();
    },
    closeAddForm() {
      this.addingForOptionId = null;
      this.addDraft = emptyDraft();
    },
    patchAddDraft(patch: Partial<ValueDraft>) {
      this.addDraft = { ...this.addDraft, ...patch };
    },
    recompute() {
      const enabled = this.options
        .filter((o) => o.enabled)
        .sort(
          (a, b) =>
            a.mapping.display_order - b.mapping.display_order ||
            a.mapping.option_id - b.mapping.option_id,
        );

      const byOption = enabled.map((o) => ({
        option_id: o.mapping.option_id,
        option_name: o.mapping.option_name,
        display_order: o.mapping.display_order,
        values: o.selectedValues
          .filter((v) => o.selectedValueIds.includes(v.id) && v.is_active)
          .map((v) => ({
            value_id: v.id,
            value: v.value,
            hex_code:
              typeof v.meta?.hex === "string"
                ? v.meta.hex
                : typeof v.meta?.hex_code === "string"
                  ? v.meta.hex_code
                  : null,
          })),
      }));

      const variants = buildVariantCombos(byOption);
      this.variantPreview = variants;
      onVariantsChange(variants);
    },
  }));

  const debouncedOptionSearch = useDebounce(ui.optionSearch, 400);

  const enabledIdsKey = ui.options
    .filter((o) => o.enabled)
    .map((o) => o.mapping.option_id)
    .sort((a, b) => a - b)
    .join(",");

  const valueSearchKey = ui.options
    .filter((o) => o.enabled)
    .map((o) => `${o.mapping.option_id}:${o.valueSearch}`)
    .sort()
    .join("|");
  const debouncedValueSearchKey = useDebounce(valueSearchKey, 400);
  const prevEnabledIdsKeyRef = React.useRef(enabledIdsKey);

  const loadOptionValues = async (
    optionId: number,
    search?: string,
  ): Promise<void> => {
    try {
      ui.setLoadingValues(optionId, true);
      const response = await catalogOptionsStore.listOptionValues(optionId, {
        page_no: 1,
        limit: 50,
        active: 1,
        search: search?.trim() || undefined,
      });
      const list = (response.list ?? []).filter((v) => v.is_active);
      runInAction(() => {
        ui.setOptionValues(optionId, list);
        ui.recompute();
      });
    } catch (error) {
      runInAction(() => ui.setLoadingValues(optionId, false));
      showError(
        "Error",
        getApiErrorMessage(error, "Could not load option values."),
      );
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!categoryId) {
        ui.setOptions([]);
        return;
      }
      try {
        ui.loading = true;
        const mappings = await categoryStore.getCategoryOptions(categoryId);
        if (cancelled) return;
        const active = (mappings ?? [])
          .filter((m) => m.active === 1)
          .sort(
            (a, b) =>
              a.display_order - b.display_order || a.option_id - b.option_id,
          );
        ui.setOptions(
          active.map((m) => ({
            mapping: m,
            enabled: false,
            loadingValues: false,
            values: [],
            valueSearch: "",
            selectedValueIds: [],
            selectedValues: [],
          })),
        );
      } catch (error) {
        if (!cancelled) {
          showError(
            "Error",
            getProductApiErrorMessage(
              error,
              "Could not load category options.",
            ),
          );
          ui.setOptions([]);
        }
      } finally {
        if (!cancelled) ui.loading = false;
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    if (resetKey === 0) return;
    ui.setOptions(
      ui.options.map((o) => ({
        ...o,
        enabled: false,
        selectedValueIds: [],
        selectedValues: [],
        valueSearch: "",
        values: [],
        loadingValues: false,
      })),
    );
    ui.closeAddForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Load values immediately when an option is enabled/disabled set changes.
  useEffect(() => {
    const enabled = ui.options.filter((o) => o.enabled);
    enabled.forEach((o) => {
      void loadOptionValues(o.mapping.option_id, o.valueSearch);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledIdsKey]);

  // Debounced refetch when value search text changes (skip the enable/disable tick).
  useEffect(() => {
    if (prevEnabledIdsKeyRef.current !== enabledIdsKey) {
      prevEnabledIdsKeyRef.current = enabledIdsKey;
      return;
    }
    if (!debouncedValueSearchKey) return;
    const enabled = ui.options.filter((o) => o.enabled);
    enabled.forEach((o) => {
      void loadOptionValues(o.mapping.option_id, o.valueSearch);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValueSearchKey]);

  const searchTerm = debouncedOptionSearch.trim().toLowerCase();
  const visibleOptions = ui.options.filter((o) => {
    if (!searchTerm) return true;
    const name = o.mapping.option_name?.toLowerCase() ?? "";
    const slug = o.mapping.option_slug?.toLowerCase() ?? "";
    return name.includes(searchTerm) || slug.includes(searchTerm);
  });

  const handleCreateValue = async (option: SkuBuilderOptionState) => {
    if (disabled || ui.savingValue || !categoryId) return;
    const optionId = option.mapping.option_id;
    const isSwatch =
      (option.mapping.option_display_type || "").toLowerCase() === "swatch";
    const positionNum = parseInt(ui.addDraft.position, 10);
    const body: CreateOptionValueBody = {
      value: ui.addDraft.value.trim(),
      sku_code: ui.addDraft.sku_code.trim(),
      position: Number.isNaN(positionNum) ? 0 : positionNum,
      is_active: ui.addDraft.is_active,
    };
    if (isSwatch) {
      body.meta = ui.addDraft.hex.trim()
        ? { hex: ui.addDraft.hex.trim() }
        : null;
    }

    const existingSkus = [...option.values, ...option.selectedValues]
      .filter((v) => v.is_active)
      .map((v) => v.sku_code ?? "")
      .filter(Boolean);
    const validationError = validateCreateOptionValueBody(body, existingSkus);
    if (validationError) {
      showError("Validation", validationError);
      return;
    }

    try {
      ui.savingValue = true;
      await catalogOptionsStore.createValues(optionId, body);
      runInAction(() => ui.closeAddForm());
      await loadOptionValues(optionId, option.valueSearch);
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to add option value."),
      );
    } finally {
      runInAction(() => {
        ui.savingValue = false;
      });
    }
  };

  if (!categoryId) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: "divider" }}
      >
        <CustomTypography variant="body3" color={colors?.greyTextColor}>
          Select a primary category first. Options are loaded from that
          category&apos;s catalog mappings.
        </CustomTypography>
      </Paper>
    );
  }

  if (ui.loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (ui.options.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: "divider" }}
      >
        <CustomTypography variant="body3" color={colors?.greyTextColor}>
          This category has no options mapped yet. Map options on the category
          detail page to configure variants.
        </CustomTypography>
      </Paper>
    );
  }

  return (
    <Grid2 container spacing={2} columns={12}>
      <Grid2 size={{ xs: 12 }}>
        <CustomTypography variant="body3" color={colors?.greyTextColor}>
          Step 1 — click options to include. Step 2 — click values under each
          option. New combinations are added on save (existing variants are
          kept).
        </CustomTypography>
      </Grid2>

      {/* <Grid2 size={{ xs: 12, md: 6 }}>
        <CustomTypography
          variant="body3"
          color={colors?.greyTextColor}
          sx={{ mb: 0.75 }}
        >
          Search options
        </CustomTypography>
        <CustomTextField
          width="100%"
          placeholder="Search by option name…"
          value={ui.optionSearch}
          disabled={disabled}
          borderColor={colors?.greyBackground}
          onChange={(e) => ui.setOptionSearch(e.target.value || "")}
          onSearch={(q) => ui.setOptionSearch(q || "")}
        />
      </Grid2> */}

      <Grid2 size={{ xs: 12, md: 6 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: colors?.purpleMain || "divider",
            height: "100%",
          }}
        >
          <CustomTypography
            variant="body3"
            color={colors?.greyTextColor}
            sx={{ mb: 0.5 }}
          >
            Product SKU (server)
          </CustomTypography>
          <CustomTypography variant="heading6">{serverSku}</CustomTypography>
        </Paper>
      </Grid2>

      <Grid2 size={{ xs: 12, md: 6 }}>
        <Paper
          variant="outlined"
          sx={{ p: 2, borderRadius: 2, borderColor: "divider", height: "100%" }}
        >
          <CustomTypography
            variant="body3"
            color={colors?.greyTextColor}
            sx={{ mb: 0.5 }}
          >
            Variant combinations
          </CustomTypography>
          <CustomTypography variant="heading6">
            {ui.variantPreview.length}
          </CustomTypography>
        </Paper>
      </Grid2>

      <Grid2 size={{ xs: 12 }}>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {visibleOptions.length === 0 ? (
            <CustomTypography variant="body3" color={colors?.greyTextColor}>
              No options match “{debouncedOptionSearch.trim()}”.
            </CustomTypography>
          ) : (
            visibleOptions.map((o) => {
              const selected = o.enabled;
              const required = o.mapping.is_required === 1;
              return (
                <Chip
                  key={o.mapping.option_id}
                  label={`${o.mapping.option_name}${required ? " · required 1" : ""} (order ${o.mapping.display_order})`}
                  clickable={!disabled}
                  onClick={() => {
                    if (disabled) return;
                    ui.toggleOption(o.mapping.option_id);
                  }}
                  color={selected ? "primary" : "default"}
                  variant={selected ? "filled" : "outlined"}
                  sx={{
                    fontWeight: selected || required ? 600 : 400,
                    borderColor: selected
                      ? colors?.purpleMain
                      : required
                        ? colors?.errorRed || theme.palette.error.main
                        : colors?.greyBorder,
                    borderWidth: required && !selected ? 1.5 : 1,
                    ...(required && !selected
                      ? {
                          color: colors?.errorRed || theme.palette.error.main,
                        }
                      : {}),
                  }}
                />
              );
            })
          )}
        </Box>
      </Grid2>

      {visibleOptions
        .filter((o) => o.enabled)
        .map((o) => {
          const isSwatch =
            (o.mapping.option_display_type || "").toLowerCase() === "swatch";
          const showAddForm = ui.addingForOptionId === o.mapping.option_id;
          return (
            <Grid2 key={o.mapping.option_id} size={{ xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: "divider",
                  backgroundColor: alpha(
                    colors?.purpleMain || theme.palette.primary.main,
                    0.03,
                  ),
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={1}
                  flexWrap="wrap"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {o.mapping.option_name}
                      {o.mapping.is_required ? " · required" : ""}
                    </CustomTypography>
                    <Box minWidth={160} maxWidth={280}>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Search Options"
                        value={o.valueSearch}
                        disabled={disabled || o.loadingValues}
                        onChange={(e) =>
                          ui.setValueSearch(
                            o.mapping.option_id,
                            e.target.value || "",
                          )
                        }
                        InputProps={{
                          disableUnderline: false,
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          },
                          "& .MuiInputBase-input": {
                            py: 0.5,
                            px: 0,
                            color: colors?.greyTextColor || "#000",
                            "&::placeholder": {
                              color: colors?.greyTextColor,
                              opacity: 1,
                            },
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: colors?.greyBorder || "#E0E0E0",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottomColor:
                                colors?.purpleMain ||
                                theme.palette.primary.main,
                            },
                          "& .MuiInput-underline:after": {
                            borderBottomColor:
                              colors?.purpleMain || theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  {!disabled ? (
                    <AppButton
                      variant="transparentPrimary"
                      label="Add value"
                      startIcon={<AddIcon />}
                      onClick={() => ui.openAddForm(o.mapping.option_id)}
                      disabled={ui.savingValue || showAddForm}
                      customStyles={{ minWidth: "auto", px: 1.5 }}
                    />
                  ) : null}
                </Box>

                <Box mb={1.5} maxWidth={360}></Box>

                {o.loadingValues ? (
                  <CircularProgress size={20} />
                ) : o.values.length === 0 && !showAddForm ? (
                  <CustomTypography
                    variant="body3"
                    color={colors?.greyTextColor}
                  >
                    {o.valueSearch.trim()
                      ? `No values match “${o.valueSearch.trim()}”.`
                      : "No active values for this option."}
                  </CustomTypography>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {o.values.map((v) => {
                      const selected = o.selectedValueIds.includes(v.id);
                      const hex =
                        typeof v.meta?.hex === "string"
                          ? v.meta.hex
                          : typeof v.meta?.hex_code === "string"
                            ? v.meta.hex_code
                            : null;
                      return (
                        <Chip
                          key={v.id}
                          clickable={!disabled}
                          onClick={() => {
                            if (disabled) return;
                            ui.toggleValue(o.mapping.option_id, v.id);
                          }}
                          label={
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              gap={0.75}
                            >
                              {hex ? (
                                <Box
                                  component="span"
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    bgcolor: hex,
                                    border: `1px solid ${colors?.greyBorder}`,
                                    display: "inline-block",
                                  }}
                                />
                              ) : null}
                              {v.value}
                            </Box>
                          }
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                        />
                      );
                    })}
                  </Box>
                )}

                {showAddForm ? (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      border: `1px dashed ${colors?.purpleMain || theme.palette.primary.main}`,
                      bgcolor: alpha(
                        colors?.purpleMain || theme.palette.primary.main,
                        0.04,
                      ),
                    }}
                  >
                    <CustomTypography variant="heading7" sx={{ mb: 1.5 }}>
                      New value for {o.mapping.option_name}
                    </CustomTypography>
                    <Grid2 container spacing={1.5} columns={12}>
                      <Grid2 size={{ xs: 12, sm: isSwatch ? 4 : 5 }}>
                        <CustomTypography
                          variant="body3"
                          color={colors?.greyTextColor}
                          sx={{ mb: 0.75 }}
                        >
                          Value *
                        </CustomTypography>
                        <CustomTextField
                          width="100%"
                          placeholder="Enter value"
                          value={ui.addDraft.value}
                          disabled={ui.savingValue}
                          onChange={(e) =>
                            ui.patchAddDraft({ value: e.target.value || "" })
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: isSwatch ? 3 : 4 }}>
                        <CustomTypography
                          variant="body3"
                          color={colors?.greyTextColor}
                          sx={{ mb: 0.75 }}
                        >
                          SKU code *
                        </CustomTypography>
                        <CustomTextField
                          width="100%"
                          placeholder="e.g. RD"
                          value={ui.addDraft.sku_code}
                          disabled={ui.savingValue}
                          onChange={(e) =>
                            ui.patchAddDraft({
                              sku_code: e.target.value || "",
                            })
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 3 }}>
                        <CustomTypography
                          variant="body3"
                          color={colors?.greyTextColor}
                          sx={{ mb: 0.75 }}
                        >
                          Position
                        </CustomTypography>
                        <CustomTextField
                          width="100%"
                          type="number"
                          allowNegativeValue={false}
                          allowDecimalValue={false}
                          placeholder="0"
                          value={ui.addDraft.position}
                          disabled={ui.savingValue}
                          onChange={(e) =>
                            ui.patchAddDraft({
                              position: e.target.value || "0",
                            })
                          }
                        />
                      </Grid2>
                      {isSwatch ? (
                        <Grid2 size={{ xs: 12, sm: 2 }}>
                          <CustomTypography
                            variant="body3"
                            color={colors?.greyTextColor}
                            sx={{ mb: 0.75 }}
                          >
                            Hex
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            placeholder="#FF0000"
                            value={ui.addDraft.hex}
                            disabled={ui.savingValue}
                            onChange={(e) =>
                              ui.patchAddDraft({ hex: e.target.value || "" })
                            }
                          />
                        </Grid2>
                      ) : null}
                      <Grid2 size={{ xs: 12 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Switch
                            checked={ui.addDraft.is_active}
                            disabled={ui.savingValue}
                            size="small"
                            onChange={(_, checked) =>
                              ui.patchAddDraft({ is_active: checked })
                            }
                          />
                          <CustomTypography
                            variant="body3"
                            color={colors?.greyTextColor}
                          >
                            {ui.addDraft.is_active ? "Active" : "Inactive"}
                          </CustomTypography>
                        </Box>
                      </Grid2>
                    </Grid2>
                    <Grid2
                      container
                      spacing={1}
                      columns={12}
                      justifyContent="flex-end"
                      sx={{ mt: 2 }}
                    >
                      <Grid2 size="auto">
                        <AppButton
                          variant="transparentPrimary"
                          label="Cancel"
                          onClick={() => ui.closeAddForm()}
                          disabled={ui.savingValue}
                        />
                      </Grid2>
                      <Grid2 size="auto">
                        <AppButton
                          variant="primary"
                          label={ui.savingValue ? "Adding…" : "Add value"}
                          onClick={() => void handleCreateValue(o)}
                          disabled={ui.savingValue}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                ) : null}
              </Paper>
            </Grid2>
          );
        })}

      {ui.variantPreview.length > 0 ? (
        <Grid2 size={{ xs: 12 }}>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {ui.variantPreview.slice(0, 24).map((v, idx) => (
              <Chip
                key={`${v.title}-${idx}`}
                size="small"
                label={v.title || v.name}
                variant="outlined"
              />
            ))}
            {ui.variantPreview.length > 24 ? (
              <CustomTypography variant="caption" color={colors?.greyTextColor}>
                +{ui.variantPreview.length - 24} more
              </CustomTypography>
            ) : null}
          </Box>
        </Grid2>
      ) : null}

      {!disabled && ui.variantPreview.length > 0 ? (
        <Grid2 size={{ xs: 12 }}>
          <AppButton
            variant="transparentPrimary"
            label="Clear selections"
            onClick={() => {
              ui.setOptions(
                ui.options.map((o) => ({
                  ...o,
                  enabled: false,
                  selectedValueIds: [],
                  selectedValues: [],
                  valueSearch: "",
                  values: [],
                  loadingValues: false,
                })),
              );
              ui.closeAddForm();
            }}
          />
        </Grid2>
      ) : null}
    </Grid2>
  );
};

export default observer(ProductSkuBuilder);
